import { useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MinutePriceGrid from '../../components/Budget/MinutePriceGrid';
import ContractABTGrid from '../../components/Budget/ContractABTGrid';
import BonusGrid from '../../components/Budget/BonusGrid';
import MonthWindowNav from '../../components/Budget/MonthWindowNav';
import LoadError from '../../components/Budget/LoadError';
import BucketSelect from '../../components/Budget/BucketSelect';
import ConfigSaveBar from '../../components/Budget/ConfigSaveBar';
import HideInactiveFilter, { useHideInactive } from '../../components/Budget/HideInactiveFilter';
import HidePastMonthsFilter, { useHidePastMonths } from '../../components/Budget/HidePastMonthsFilter';
import HintIcon from '../../components/Budget/HintIcon';
import UnsavedEditsPrompt from '../../components/Budget/UnsavedEditsPrompt';
import { useConfigRowLabels } from '../../components/Budget/configEditIssues';
import { useGlobalBillingConfigs } from '../../hooks/api/budgetBillingConfig';
import { usePendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { useBudgetContext } from '../../store/Budget/BudgetContext';

interface SectionHeadingProps {
    title: string;
    /** What this particular series is and how it behaves — behind the `?`, not under the title. */
    notes: string[];
    hidePastMonths: boolean;
    onHidePastMonthsChange: (value: boolean) => void;
}

/**
 * The heading row the three sections share: title, its notes behind a `?`, the past-months filter, the
 * year nav.
 *
 * One component because the three must not drift — and because what used to sit under each title was the
 * SAME paragraph three times, which is both useless and the row of space the filter now occupies.
 *
 * The filter is repeated per section rather than kept in the page toolbar: it is the columns of the grid
 * right below it that it takes away, and all three read one state, so ticking any of them ticks all three.
 *
 * CALL-815: the nav deliberately keeps the default `rangeBehaviour` of `ignore`. This page's three grids call
 * getWindowMonths, so they show the 12-month window whatever `Datum von`/`bis` the Overview left set — and
 * the page has no range control to clear it with, so a range-following nav here would only disable its own
 * arrows (or, worse, hide them) with no way back short of returning to the Overview.
 */
const SectionHeading = ({ title, notes, hidePastMonths, onHidePastMonthsChange }: SectionHeadingProps) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
                <span>{title}</span>
            </Typography>
            <HintIcon notes={notes} />
            <Box sx={{ ml: 1 }}>
                <HidePastMonthsFilter checked={hidePastMonths} onChange={onHidePastMonthsChange} />
            </Box>
        </Box>
        <MonthWindowNav />
    </Box>
);

// CALL-745: minute prices, contract ABT and bonuses are global master data — version-agnostic.
// CALL-758: scoped to a bucket — the bucket selector is the first page filter, and prices/ABT only
// show the selected bucket's configs (within a bucket each WUL is unique again).
// (Per-version forecast volumes and their "Reload Data" action live on the Overview.)
// CALL-779: ONE pending set for all three grids, and one Save at the bottom. They edit three different
// monthly series of the SAME config rows, so a single request carries a page's worth of edits and one
// transaction decides whether all of them land.
const BudgetPricesAbt = () => {
    const { t } = useTranslation('budget');
    // CALL-758: the bucket filter is the first control; "All buckets" shows every bucket's configs.
    const { buckets, selectedBucketId, setSelectedBucketId, canEdit } = useBudgetContext();
    const [billingConfigs, isLoading, { isError, error, refetch }] = useGlobalBillingConfigs(selectedBucketId);
    // Survives the bucket and location/working-unit filters: the store is keyed by config id, so an
    // edit made under one filter is still pending (and still saved) under another.
    const pending = usePendingConfigEdits();
    // CALL-778: one storage key with the Overview's filter, so ticking it in either place holds in both.
    const [hideInactive, setHideInactive] = useHideInactive();
    // CALL-781: same one-key sharing as the visibility filter — this page is where months are typed in,
    // so the past ones are exactly the columns nobody needs in front of them.
    const [hidePastMonths, setHidePastMonths] = useHidePastMonths();

    // Top-of-page filter shared by all three grids (they render the same config list). Location is
    // keyed by HEMT locationId; Working Unit by its display name. Both are multi-select; a row must
    // pass both filters (AND), matching any selected value within a filter (OR).
    const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
    const [selectedWorkingUnits, setSelectedWorkingUnits] = useState<string[]>([]);

    // Named rows for the save bar: a blocking edit can sit in a row the filters below are hiding — or one
    // a bucket switch has taken out of the query entirely — so labels come from the unfiltered list and
    // are accumulated across fetches.
    const rowLabels = useConfigRowLabels(billingConfigs);

    // Stable label lookup for every location, so a selected location keeps its chip label even when
    // the cross-filter below would otherwise drop it from the option list.
    const locationLabels = useMemo(() => {
        const byId = new Map<number, string>();
        for (const config of billingConfigs) {
            if (config.locationId == null || byId.has(config.locationId)) continue;
            const label = [config.partnerName, config.locationName].filter(Boolean).join(' – ');
            byId.set(config.locationId, label || String(config.locationId));
        }
        return byId;
    }, [billingConfigs]);

    // Dependent options: each dropdown shrinks to the values available under the *other* dropdown's
    // selection, but always keeps the current selection so its chips stay visible and removable.
    const locationOptions = useMemo(() => {
        const ids = new Set<number>(selectedLocationIds);
        for (const config of billingConfigs) {
            if (config.locationId == null) continue;
            const matchesWorkingUnit =
                selectedWorkingUnits.length === 0 || selectedWorkingUnits.includes(config.workingUnitName);
            if (matchesWorkingUnit) ids.add(config.locationId);
        }
        return Array.from(ids, (locationId) => ({
            locationId,
            label: locationLabels.get(locationId) ?? String(locationId),
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [billingConfigs, selectedWorkingUnits, selectedLocationIds, locationLabels]);

    const workingUnitOptions = useMemo(() => {
        const names = new Set<string>(selectedWorkingUnits);
        for (const config of billingConfigs) {
            if (!config.workingUnitName) continue;
            const matchesLocation =
                selectedLocationIds.length === 0 ||
                (config.locationId != null && selectedLocationIds.includes(config.locationId));
            if (matchesLocation) names.add(config.workingUnitName);
        }
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [billingConfigs, selectedLocationIds, selectedWorkingUnits]);

    const filteredConfigs = useMemo(
        () =>
            billingConfigs.filter((config) => {
                const locationOk =
                    selectedLocationIds.length === 0 ||
                    (config.locationId != null && selectedLocationIds.includes(config.locationId));
                const workingUnitOk =
                    selectedWorkingUnits.length === 0 || selectedWorkingUnits.includes(config.workingUnitName);
                return locationOk && workingUnitOk;
            }),
        [billingConfigs, selectedLocationIds, selectedWorkingUnits]
    );

    // Every edit in this page's store belongs to a config row (its three grids edit three monthly series of
    // the same rows), so any pending row id counts — unlike the Overview, whose store is shared with the
    // on-top grid and needs the kind checked.
    const editedConfigIds = useMemo(() => new Set(pending.edits.map((edit) => edit.rowId)), [pending.edits]);

    // CALL-778: obsolete partners are dropped from the price/ABT/bonus grids too — the client confirmed it
    // is exactly the rows they do not want in front of them while filling these in. Applied AFTER the
    // location / working-unit filters, so the count below reports only what THIS filter took away.
    //
    // A row with unsaved edits is never hidden: this filter is on by default, so unlike the two dropdowns
    // above it can take a row away without the user having asked for it — and the save bar would then name
    // a blocking edit in a row that is not on screen.
    const visibleConfigs = useMemo(
        () =>
            hideInactive
                ? filteredConfigs.filter((config) => !config.isHidden || editedConfigIds.has(config.id))
                : filteredConfigs,
        [filteredConfigs, hideInactive, editedConfigIds]
    );

    if (isError) return <LoadError error={error} onRetry={refetch} />;

    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {/* CALL-758: bucket is the first filter — "All buckets" shows every bucket's configs. */}
                <BucketSelect
                    buckets={buckets}
                    value={selectedBucketId}
                    onChange={setSelectedBucketId}
                    minWidth={220}
                />
                <Autocomplete
                    multiple
                    size="small"
                    limitTags={3}
                    options={locationOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
                    value={locationOptions.filter((option) => selectedLocationIds.includes(option.locationId))}
                    onChange={(_, value) => setSelectedLocationIds(value.map((option) => option.locationId))}
                    sx={{ minWidth: 280, flex: 1 }}
                    renderInput={(params) => <TextField {...params} label={t('field.location')} />}
                />
                <Autocomplete
                    multiple
                    size="small"
                    limitTags={3}
                    options={workingUnitOptions}
                    value={selectedWorkingUnits}
                    onChange={(_, value) => setSelectedWorkingUnits(value)}
                    sx={{ minWidth: 280, flex: 1 }}
                    renderInput={(params) => <TextField {...params} label={t('field.workingUnit')} />}
                />
                {/* Alongside the other two page filters — it is one: it drops ROWS, from all three grids.
                    (The past-months filter is per section, in each heading below.) */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HideInactiveFilter checked={hideInactive} onChange={setHideInactive} />
                </Box>
            </Box>

            <SectionHeading
                title={t('prices.minutePrices')}
                notes={[t('hint.minutePricesSection'), t('hint.rowsFromBillingConfig')]}
                hidePastMonths={hidePastMonths}
                onHidePastMonthsChange={setHidePastMonths}
            />
            <MinutePriceGrid
                billingConfigs={visibleConfigs}
                isLoading={isLoading}
                pending={pending}
                hidePastMonths={hidePastMonths}
            />

            <Box sx={{ mt: 4 }} />

            <SectionHeading
                title={t('prices.contractAbt')}
                notes={[t('hint.contractAbtSection'), t('hint.rowsFromBillingConfig')]}
                hidePastMonths={hidePastMonths}
                onHidePastMonthsChange={setHidePastMonths}
            />
            <ContractABTGrid
                billingConfigs={visibleConfigs}
                isLoading={isLoading}
                pending={pending}
                hidePastMonths={hidePastMonths}
            />

            <Box sx={{ mt: 4 }} />

            <SectionHeading
                title={t('prices.bonus')}
                notes={[t('hint.bonusSection'), t('hint.rowsFromBillingConfig')]}
                hidePastMonths={hidePastMonths}
                onHidePastMonthsChange={setHidePastMonths}
            />
            <BonusGrid
                billingConfigs={visibleConfigs}
                isLoading={isLoading}
                pending={pending}
                hidePastMonths={hidePastMonths}
            />

            {/* Outside the canEdit branch, so the guard cannot vanish with the save bar while edits are
                pending (see BudgetBillingConfig). */}
            <UnsavedEditsPrompt isDirty={pending.hasEdits} />
            {/* The rate grids paint extrapolated cells (CALL-752); the Billing Config grid does not. */}
            {canEdit && (
                <ConfigSaveBar
                    pending={pending}
                    rowLabels={rowLabels}
                    legendCues={['editable', 'unsaved', 'extrapolated', 'defaultOverride']}
                />
            )}
        </>
    );
};

export default BudgetPricesAbt;
